import React, { useState, useEffect } from 'react';
import { createClient, updateClient } from '../utils/api';
import ClientAiProfile from './ClientAiProfile';
import { ar, en } from '../utils/translations';
import { convertArabicNumerals } from '../utils/numberUtils';
import { COUNTRY_CODES, splitPhoneAndCode as sharedSplitPhone } from '../utils/countryCodes';

const ClientModal = ({ client, onClose, language = 'ar', viewMode = false, userRole = 'owner' }) => {
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';
  
  // Saudi Arabia cities list
  const saudiCities = [
    { value: '', label: language === 'ar' ? 'اختر المدينة' : 'Select City' },
    { value: 'riyadh', label: language === 'ar' ? 'الرياض' : 'Riyadh' },
    { value: 'jeddah', label: language === 'ar' ? 'جدة' : 'Jeddah' },
    { value: 'dammam', label: language === 'ar' ? 'الدمام' : 'Dammam' },
    { value: 'makkah', label: language === 'ar' ? 'مكة المكرمة' : 'Makkah' },
    { value: 'madinah', label: language === 'ar' ? 'المدينة المنورة' : 'Madinah' },
    { value: 'khobar', label: language === 'ar' ? 'الخبر' : 'Khobar' },
    { value: 'taif', label: language === 'ar' ? 'الطائف' : 'Taif' },
    { value: 'abha', label: language === 'ar' ? 'أبها' : 'Abha' },
    { value: 'khamis_mushait', label: language === 'ar' ? 'خميس مشيط' : 'Khamis Mushait' },
    { value: 'hail', label: language === 'ar' ? 'حائل' : 'Hail' },
    { value: 'najran', label: language === 'ar' ? 'نجران' : 'Najran' },
    { value: 'jazan', label: language === 'ar' ? 'جازان' : 'Jazan' },
    { value: 'yanbu', label: language === 'ar' ? 'ينبع' : 'Yanbu' },
    { value: 'buraidah', label: language === 'ar' ? 'بريدة' : 'Buraidah' },
    { value: 'tabuk', label: language === 'ar' ? 'تبوك' : 'Tabuk' },
    { value: 'qatif', label: language === 'ar' ? 'القطيف' : 'Qatif' },
    { value: 'kharj', label: language === 'ar' ? 'الخرج' : 'Al Kharj' },
    { value: 'hafr_albatin', label: language === 'ar' ? 'حفر الباطن' : 'Hafr Al-Batin' },
    { value: 'other', label: language === 'ar' ? 'أخرى' : 'Other' }
  ];
  
  // Country codes list (same as login page)
  const countryCodes = COUNTRY_CODES;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    whatsapp: '',
    address: '',
    city: '',
    city_other: '', // Custom city name when "other" is selected
    address_details: '', // Street address and additional details (will be combined with city)
    notes: '',
    importance: 'medium', // high, medium, low (3 options)
    property_preference: [], // Array for multiple selections
    interest_type: [], // Array for multiple selections
    budget: '',
    budget_min: '',
    budget_max: '',
    property_preference_other: '', // Custom property type when "other" is selected
    customer_type: 'prospect', // 'prospect' or 'customer'
    source: '', // Optional source field
    source_other: '' // Custom source text when "other" is selected
  });
  const [loading, setLoading] = useState(false);
  
  // Phone country codes - Remember last used
  const [phoneCountryCode, setPhoneCountryCode] = useState(() => {
    return localStorage.getItem('lastPhoneCountryCode') || '+966';
  });
  const [whatsappCountryCode, setWhatsappCountryCode] = useState(() => {
    return localStorage.getItem('lastWhatsappCountryCode') || '+966';
  });

  // Update localStorage when country code changes
  const handlePhoneCountryCodeChange = (code) => {
    setPhoneCountryCode(code);
    localStorage.setItem('lastPhoneCountryCode', code);
  };

  const handleWhatsappCountryCodeChange = (code) => {
    setWhatsappCountryCode(code);
    localStorage.setItem('lastWhatsappCountryCode', code);
  };
  
  // NEW: Enhanced form state management
  const [showFullForm, setShowFullForm] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    contact: true,      // Contact details expanded by default
    preferences: false, // Preferences collapsed
    additional: false   // Additional info collapsed
  });

  useEffect(() => {
    if (client) {
      // If editing, show full form and expand all sections
      setShowFullForm(true);
      setExpandedSections({ contact: true, preferences: true, additional: true });
      
      // Parse existing budget if it's in range format (e.g., "500000 - 800000")
      let budgetMin = '';
      let budgetMax = '';
      if (client.budget) {
        const budgetStr = client.budget.toString();
        if (budgetStr.includes('-') || budgetStr.includes('–')) {
          const parts = budgetStr.split(/[-–]/).map(p => p.trim().replace(/[^\d]/g, ''));
          budgetMin = parts[0] || '';
          budgetMax = parts[1] || '';
        } else {
          // Single value, treat as min
          budgetMin = budgetStr.replace(/[^\d]/g, '');
        }
      }
      
      // Parse phone and whatsapp to extract country codes
      const { code: phoneCode, local: phoneNum } = sharedSplitPhone(client.phone);
      const { code: whatsappCode, local: whatsappNum } = sharedSplitPhone(client.whatsapp);

      setPhoneCountryCode(phoneCode);
      setWhatsappCountryCode(whatsappCode);
      // Remember country codes in localStorage
      localStorage.setItem('lastPhoneCountryCode', phoneCode);
      localStorage.setItem('lastWhatsappCountryCode', whatsappCode);
      
      // Parse comma-separated strings to arrays
      const parseArrayField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        // Check if there's custom text after colon (format: "other:custom text")
        if (field.includes(':')) {
          const parts = field.split(':');
          return parts[0].split(',').map(item => item.trim()).filter(item => item);
        }
        return field.split(',').map(item => item.trim()).filter(item => item);
      };

      // Extract custom property preference text if exists
      const extractCustomPropertyText = (field) => {
        if (!field) return '';
        if (field.includes(':')) {
          const parts = field.split(':');
          return parts.slice(1).join(':'); // In case custom text contains colons
        }
        return '';
      };

      // Parse address: format is "city|address_details" or "other:city_name|address_details" or just address for backward compatibility
      const parseAddress = (address) => {
        if (!address) return { city: '', city_other: '', address_details: '' };
        if (address.includes('|')) {
          const parts = address.split('|');
          const cityPart = parts[0] || '';
          if (cityPart.startsWith('other:')) {
            const customCity = cityPart.replace('other:', '');
            return { city: 'other', city_other: customCity, address_details: parts.slice(1).join('|') || '' };
          }
          return { city: cityPart, city_other: '', address_details: parts.slice(1).join('|') || '' };
        }
        // Backward compatibility: if no pipe, just put everything in address_details
        return { city: '', city_other: '', address_details: address };
      };

      const parsedAddress = parseAddress(client.address || '');
      
      setFormData({
        name: client.name || '',
        phone: phoneNum,
        email: client.email || '',
        whatsapp: whatsappNum,
        address: client.address || '',
        city: parsedAddress.city,
        city_other: parsedAddress.city_other,
        address_details: parsedAddress.address_details,
        notes: client.notes || '',
        importance: client.importance || 'medium',
        property_preference: parseArrayField(client.property_preference),
        interest_type: parseArrayField(client.interest_type),
        budget: client.budget || '',
        budget_min: budgetMin,
        budget_max: budgetMax,
        property_preference_other: extractCustomPropertyText(client.property_preference || ''),
        customer_type: client.customer_type || 'prospect',
        source: client.source ? (client.source.startsWith('other:') ? 'other' : client.source) : '',
        source_other: client.source && client.source.startsWith('other:') ? client.source.substring(6) : ''
      });
    } else {
      // New client - start with quick add mode
      setShowFullForm(false);
      setExpandedSections({ contact: true, preferences: false, additional: false });
    }
  }, [client]);


  // Strip any country-code prefix the user accidentally typed into the local number field.
  // e.g. if the field already has +966 selected and they paste "+966 568019465" → "568019465"
  const sanitizeLocalPhone = (value, countryCode) => {
    let v = convertArabicNumerals(value);
    // Remove leading + and whitespace
    v = v.replace(/^\+/, '').trim();
    // Strip the digits of the selected country code if they appear at the start
    const codeDigits = countryCode.replace(/\D/g, '');
    if (codeDigits && v.startsWith(codeDigits)) {
      v = v.slice(codeDigits.length).trim();
    }
    // Guard against doubled generic prefix (e.g. "966966…")
    const m = v.match(/^(\d{1,4})\1(\d{6,})$/);
    if (m) v = v.slice(m[1].length);
    // Strip leading trunk zero (e.g. "0568019465" → "568019465")
    v = v.replace(/^0+/, '');
    return v;
  };

  const handleChange = (e) => {
    let value = e.target.value;
    
    // Convert Arabic numerals to Western numerals for phone fields + auto-correct country prefix
    if (e.target.name === 'phone') {
      value = sanitizeLocalPhone(value, phoneCountryCode);
    } else if (e.target.name === 'whatsapp') {
      value = sanitizeLocalPhone(value, whatsappCountryCode);
    }
    
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  // NEW: Quick interest type selection (toggle in array)
  const handleQuickInterest = (type) => {
    setFormData(prev => {
      const currentTypes = Array.isArray(prev.interest_type) ? prev.interest_type : (prev.interest_type ? [prev.interest_type] : []);
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter(t => t !== type) // Remove if already selected
        : [...currentTypes, type]; // Add if not selected
      return { ...prev, interest_type: newTypes };
    });
    if (!showFullForm) {
      setShowFullForm(true);
      setExpandedSections(prev => ({ ...prev, preferences: true }));
    }
  };

  // NEW: Quick property preference selection (toggle in array)
  const handleQuickProperty = (type) => {
    setFormData(prev => {
      const currentPrefs = Array.isArray(prev.property_preference) ? prev.property_preference : (prev.property_preference ? [prev.property_preference] : []);
      const newPrefs = currentPrefs.includes(type)
        ? currentPrefs.filter(p => p !== type) // Remove if already selected
        : [...currentPrefs, type]; // Add if not selected
      return { ...prev, property_preference: newPrefs };
    });
    if (!showFullForm) {
      setShowFullForm(true);
      setExpandedSections(prev => ({ ...prev, preferences: true }));
    }
  };

  // Handler for toggling interest type in full form
  const handleInterestTypeToggle = (type) => {
    setFormData(prev => {
      const currentTypes = Array.isArray(prev.interest_type) ? prev.interest_type : (prev.interest_type ? [prev.interest_type] : []);
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter(t => t !== type)
        : [...currentTypes, type];
      return { ...prev, interest_type: newTypes };
    });
  };

  // Handler for source selection (single selection - clicking same button clears it, different button changes it)
  const handleSourceSelect = (source) => {
    setFormData(prev => {
      // If clicking the same source, clear it (toggle off)
      // If clicking a different source, set it (toggle on)
      const newSource = prev.source === source ? '' : source;
      return { ...prev, source: newSource };
    });
  };

  // Source options (most common and analyzable)
  const sourceOptions = [
    { value: 'website', label: { ar: 'موقع إلكتروني', en: 'Website' }, icon: '🌐' },
    { value: 'social_media', label: { ar: 'وسائل التواصل الاجتماعي', en: 'Social Media' }, icon: '📱' },
    { value: 'referral', label: { ar: 'إحالة', en: 'Referral' }, icon: '👥' },
    { value: 'advertisement', label: { ar: 'إعلان', en: 'Advertisement' }, icon: '📢' },
    { value: 'visit', label: { ar: 'زيارة', en: 'Visit' }, icon: '🚶' }
  ];

  // Handler for toggling property preference in full form
  const handlePropertyPreferenceToggle = (type) => {
    setFormData(prev => {
      const currentPrefs = Array.isArray(prev.property_preference) ? prev.property_preference : (prev.property_preference ? [prev.property_preference] : []);
      const newPrefs = currentPrefs.includes(type)
        ? currentPrefs.filter(p => p !== type)
        : [...currentPrefs, type];
      return { ...prev, property_preference: newPrefs };
    });
  };


  // NEW: Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // NEW: Format budget input (numbers only)
  const formatBudgetInput = (value) => {
    // Remove all non-digit characters
    return value.replace(/[^\d]/g, '');
  };

  // Format number with commas (e.g., 500000 -> 500,000)
  const formatNumberWithCommas = (num) => {
    if (!num) return '';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to get input/select styles for view mode
  const getInputStyle = (baseStyle) => ({
    ...baseStyle,
    background: viewMode ? '#f5f5f5' : (baseStyle.background || '#ffffff'),
    cursor: viewMode ? 'default' : (baseStyle.cursor || 'text')
  });

  // Validation functions
  const isValidEmail = (email) => {
    if (!email) return null; // Return null if empty (not required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    if (!phone) return null; // Return null if empty (optional)
    // Basic validation: at least 7 digits
    return /^\d{7,}$/.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const isValidBudgetRange = () => {
    if (!formData.budget_min && !formData.budget_max) return null;
    if (!formData.budget_max) return true; // Max is optional
    if (!formData.budget_min) return true; // Min is optional if max exists
    const min = parseInt(formData.budget_min.replace(/[^\d]/g, ''), 10);
    const max = parseInt(formData.budget_max.replace(/[^\d]/g, ''), 10);
    if (isNaN(min) || isNaN(max)) return null;
    return max >= min;
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const form = e.target.closest('form');
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton && !loading) {
            submitButton.click();
          }
        }
      }
      // Esc to close (only if not in an input field)
      if (e.key === 'Escape' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, onClose]);

  const handleBudgetMinChange = (e) => {
    // Convert Arabic numerals to Western numerals before formatting
    const convertedValue = convertArabicNumerals(e.target.value);
    const formatted = formatBudgetInput(convertedValue);
    setFormData({
      ...formData,
      budget_min: formatted,
      budget: formatted && formData.budget_max 
        ? `${formatted} - ${formData.budget_max}` 
        : formatted || formData.budget || ''
    });
  };

  const handleBudgetMaxChange = (e) => {
    // Convert Arabic numerals to Western numerals before formatting
    const convertedValue = convertArabicNumerals(e.target.value);
    const formatted = formatBudgetInput(convertedValue);
    setFormData({
      ...formData,
      budget_max: formatted,
      budget: formData.budget_min && formatted
        ? `${formData.budget_min} - ${formatted}`
        : formData.budget_min || formatted || ''
    });
  };

  const handleSubmit = async (e, saveAndAddAnother = false) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(t.nameRequired);
      return;
    }

    // Validate budget range
    const budgetValidation = isValidBudgetRange();
    if (budgetValidation === false) {
      alert(language === 'ar' 
        ? 'الحد الأقصى للميزانية يجب أن يكون أكبر من أو يساوي الحد الأدنى'
        : 'Maximum budget must be greater than or equal to minimum budget');
      return;
    }

    setLoading(true);
    try {
      // Combine country codes with phone numbers
      // Convert arrays to comma-separated strings for database
      // Combine city and address_details into address field (format: "city|address_details" or "other:city_name|address_details")
      const getCityValue = () => {
        if (formData.city === 'other' && formData.city_other) {
          return `other:${formData.city_other}`;
        }
        return formData.city || '';
      };
      
      const cityValue = getCityValue();
      const combinedAddress = cityValue && formData.address_details
        ? `${cityValue}|${formData.address_details}`
        : cityValue
        ? cityValue
        : formData.address_details || formData.address || '';

      // Handle source field - if "other" is selected, combine with custom text
      const sourceValue = formData.source === 'other' && formData.source_other
        ? `other:${formData.source_other}`
        : formData.source || null;

      const submitData = {
        ...formData,
        phone: formData.phone ? phoneCountryCode + sanitizeLocalPhone(formData.phone, phoneCountryCode).replace(/[\s\-\(\)]/g, '') : '',
        whatsapp: formData.whatsapp ? whatsappCountryCode + sanitizeLocalPhone(formData.whatsapp, whatsappCountryCode).replace(/[\s\-\(\)]/g, '') : '',
        address: combinedAddress,
        interest_type: Array.isArray(formData.interest_type) ? formData.interest_type.join(',') : formData.interest_type || '',
        property_preference: (() => {
          const prefs = Array.isArray(formData.property_preference) ? formData.property_preference : (formData.property_preference ? [formData.property_preference] : []);
          // If "other" is selected and there's custom text, append it
          if (prefs.includes('other') && formData.property_preference_other) {
            return prefs.join(',') + ':' + formData.property_preference_other;
          }
          return prefs.join(',');
        })(),
        source: sourceValue
      };
      
      // Remove internal fields before submitting
      delete submitData.city;
      delete submitData.city_other;
      delete submitData.address_details;
      delete submitData.budget_min;
      delete submitData.budget_max;
      delete submitData.property_preference_other;
      delete submitData.source_other;
      
      if (client) {
        await updateClient(client.id, submitData);
      } else {
        await createClient(submitData);
      }
      
      if (saveAndAddAnother) {
        // Reset form but keep modal open
        setFormData({
          name: '',
          phone: '',
          email: '',
          whatsapp: '',
          address: '',
          city: '',
          city_other: '',
          address_details: '',
          notes: '',
          importance: 'medium',
          property_preference: [],
          interest_type: [],
          budget: '',
          budget_min: '',
          budget_max: '',
          property_preference_other: '',
          customer_type: 'prospect',
          source: '',
          source_other: ''
        });
        setShowFullForm(false);
        setExpandedSections({ contact: true, preferences: false, additional: false });
        // Focus on name field for next entry
        setTimeout(() => {
          document.querySelector('input[name="name"]')?.focus();
        }, 100);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error saving client:', error);
      alert(t.failedToSave);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>{viewMode ? (language === 'ar' ? 'عرض العميل' : 'View Client') : (client ? t.editClient : t.addNewClient)}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {client?.id && (
          <div style={{ padding: '0 20px' }}>
            <ClientAiProfile
              clientId={client.id}
              language={language}
              isOwner={userRole === 'owner' || userRole === 'manager'}
            />
          </div>
        )}
        <form onSubmit={(e) => { if (!viewMode) handleSubmit(e, false); else e.preventDefault(); }}>
          {/* ===== SECTION 1: ESSENTIAL INFO (Always Visible) ===== */}
          <div style={{ marginBottom: '20px' }}>
            <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
              <label style={{ textAlign: isRTL ? 'right' : 'left' }}>
                {t.name} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={viewMode}
                autoFocus={!viewMode}
                placeholder={t.namePlaceholder || (language === 'ar' ? 'الاسم الكامل' : 'Full name')}
                style={{ 
                  width: '100%',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  background: viewMode ? '#f5f5f5' : '#ffffff',
                  cursor: viewMode ? 'default' : 'text'
                }}
              />
            </div>

            <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
              <label style={{ textAlign: isRTL ? 'right' : 'left' }}>{t.phone || 'Phone'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="phone-input-wrapper" style={{ direction: 'ltr', width: '100%', maxWidth: '280px', flex: 1 }}>
                <select
                  value={phoneCountryCode}
                    onChange={(e) => handlePhoneCountryCodeChange(e.target.value)}
                    disabled={viewMode}
                  className="country-code-select"
                  style={{
                      padding: '8px 6px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                      background: viewMode ? '#f5f5f5' : '#fafafa',
                      cursor: viewMode ? 'default' : 'pointer',
                    width: '110px',
                    minWidth: '110px',
                    maxWidth: '110px',
                    flexShrink: 0
                  }}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                    disabled={viewMode}
                  placeholder={language === 'ar' ? 'مثال: 501234567' : 'Example: 501234567'}
                  className="phone-input"
                  style={{
                    flex: 1,
                    minWidth: '0',
                      maxWidth: '170px',
                      padding: '8px 12px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: viewMode ? '#f5f5f5' : '#fafafa',
                      cursor: viewMode ? 'default' : 'text',
                    direction: 'ltr'
                  }}
                />
                </div>
                {formData.phone && isValidPhone(formData.phone) !== null && (
                  <span style={{ fontSize: '1.2rem', color: isValidPhone(formData.phone) ? '#10b981' : '#ef4444' }}>
                    {isValidPhone(formData.phone) ? '✓' : '✗'}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
              <label style={{ textAlign: isRTL ? 'right' : 'left' }}>
                {language === 'ar' ? 'رقم آخر (اختياري)' : 'Another Number (Optional)'}
              </label>
              <div className="phone-input-wrapper" style={{ direction: 'ltr', width: '100%', maxWidth: '280px' }}>
                <select
                  value={whatsappCountryCode}
                  onChange={(e) => handleWhatsappCountryCodeChange(e.target.value)}
                  disabled={viewMode}
                  className="country-code-select"
                  style={{
                    padding: '8px 6px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    background: viewMode ? '#f5f5f5' : '#fafafa',
                    cursor: viewMode ? 'default' : 'pointer',
                    width: '110px',
                    minWidth: '110px',
                    maxWidth: '110px',
                    flexShrink: 0
                  }}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder={language === 'ar' ? 'مثال: 501234567' : 'Example: 501234567'}
                  className="phone-input"
                  style={{
                    flex: 1,
                    minWidth: '0',
                    maxWidth: '170px',
                    padding: '8px 12px',
                    border: '2px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    background: '#fafafa',
                    direction: 'ltr'
                  }}
                />
              </div>
            </div>

            {/* Quick Interest Type Buttons (Quick Add Mode) */}
            {!showFullForm && (
              <>
                <div className="form-group">
                  <label style={{ marginBottom: '8px' }}>{t.quickInterest || 'Quick Interest:'}</label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {['buy', 'rent', 'sell'].map((type) => {
                      const interestTypes = Array.isArray(formData.interest_type) ? formData.interest_type : (formData.interest_type ? [formData.interest_type] : []);
                      const isSelected = interestTypes.includes(type);
                      const styles = {
                        buy: { color: '#10b981', bgColor: '#d1fae5', textColor: '#065f46' },
                        rent: { color: '#3b82f6', bgColor: '#dbeafe', textColor: '#1e40af' },
                        sell: { color: '#f59e0b', bgColor: '#fef3c7', textColor: '#92400e' }
                      };
                      const labels = {
                        buy: { icon: '🏠', text: t.buy || 'Buy' },
                        rent: { icon: '🏘️', text: t.rent || 'Rent' },
                        sell: { icon: '💰', text: t.sell || 'Sell' }
                      };
                      return (
                    <button
                          key={type}
                      type="button"
                          onClick={() => handleQuickInterest(type)}
                      style={{
                            padding: '6px 12px',
                            border: isSelected ? `2px solid ${styles[type].color}` : '1px solid #e5e7eb',
                        borderRadius: '6px',
                            background: isSelected ? styles[type].bgColor : '#fff',
                        cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? '600' : '400',
                            color: isSelected ? styles[type].textColor : '#4b5563',
                            transition: 'all 0.2s ease'
                      }}
                    >
                          {labels[type].icon} {labels[type].text}
                    </button>
                      );
                    })}
                  </div>
                </div>

              </>
            )}

            {/* Client Importance - 3 Options Color Rating System */}
            <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
              <label style={{ textAlign: isRTL ? 'right' : 'left', marginBottom: '8px', fontSize: '0.95rem' }}>
                {language === 'ar' ? 'أهمية العميل' : 'Client Importance'}
              </label>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { value: 'high', label: language === 'ar' ? 'عالية' : 'High', icon: '🔴', color: '#ef4444', bgColor: '#fee2e2' },
                  { value: 'medium', label: language === 'ar' ? 'متوسطة' : 'Medium', icon: '🟡', color: '#f59e0b', bgColor: '#fef3c7' },
                  { value: 'low', label: language === 'ar' ? 'منخفضة' : 'Low', icon: '🟢', color: '#10b981', bgColor: '#d1fae5' }
                ].map((level) => {
                  const isSelected = formData.importance === level.value;
                  
                  return (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => handleChange({ target: { name: 'importance', value: level.value } })}
                      style={{
                        padding: '4px 10px',
                        border: 'none',
                        borderRadius: '4px',
                        background: isSelected 
                          ? level.bgColor 
                          : 'transparent',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: isSelected ? '500' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: isSelected ? level.color : '#9ca3af',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = '#f3f4f6';
                          e.currentTarget.style.color = level.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#9ca3af';
                        }
                      }}
                      title={level.label}
                    >
                      <span style={{ fontSize: '0.85rem' }}>{level.icon}</span>
                      <span>{level.label}</span>
                    </button>
                  );
                })}
                  </div>
                </div>

            {/* Expand to Full Form Button (Quick Add Mode only) */}
            {!showFullForm && (
              <button
                type="button"
                onClick={() => setShowFullForm(true)}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '10px',
                  background: '#f5f5f5',
                  border: '1px dashed #ccc',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: '#666'
                }}
              >
                ➕ {t.addMoreDetails || 'Add More Details'}
              </button>
            )}
          </div>

          {/* ===== SECTION 2: ADDITIONAL CONTACT (Collapsible) ===== */}
          {showFullForm && (
            <>
              <div style={{ 
                marginTop: '20px', 
                paddingTop: '20px', 
                borderTop: '2px solid #e0e0e0' 
              }}>
                <div
                  onClick={() => toggleSection('contact')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    padding: '8px',
                    borderRadius: '4px',
                    background: expandedSections.contact ? '#f9f9f9' : 'transparent'
                  }}
                >
                  <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: '600' }}>
                    {t.additionalContact || 'Additional Contact'}
                  </h3>
                  <span style={{ fontSize: '1.2rem' }}>
                    {expandedSections.contact ? '▼' : '▶'}
                  </span>
                </div>

                {expandedSections.contact && (
                  <div>
                    <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
                      <label style={{ textAlign: isRTL ? 'right' : 'left' }}>{t.email}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t.emailPlaceholder || (language === 'ar' ? 'example@email.com' : 'email@example.com')}
                        style={{ 
                          width: '100%',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      />
                    </div>
                    <div className="form-grid" style={{ marginTop: '6px' }}>
                      <div className="form-group form-col-4">
                        <label style={{ textAlign: isRTL ? 'right' : 'left', marginBottom: '8px' }}>
                          {language === 'ar' ? 'المدينة' : 'City'}
                        </label>
                        <select
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          disabled={viewMode}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '2px solid #e8e8e8',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            background: viewMode ? '#f5f5f5' : '#fafafa',
                            cursor: viewMode ? 'default' : 'pointer',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                        >
                          {saudiCities.map((city) => (
                            <option key={city.value} value={city.value}>
                              {city.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group form-col-8">
                        <label style={{ textAlign: isRTL ? 'right' : 'left' }}>
                          {language === 'ar' ? 'تفاصيل العنوان' : 'Address Details'}
                        </label>
                        <input
                          type="text"
                          name="address_details"
                          value={formData.address_details}
                          onChange={handleChange}
                          disabled={viewMode}
                          placeholder={language === 'ar' ? 'الحي، الشارع، رقم المبنى' : 'District, Street, Building Number'}
                          style={{ 
                            width: '100%',
                            padding: '8px 12px',
                            border: '2px solid #e8e8e8',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            background: viewMode ? '#f5f5f5' : '#ffffff',
                            cursor: viewMode ? 'default' : 'text',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                        />
                      </div>

                      {formData.city === 'other' && (
                        <div className="form-group form-col-12">
                          <label style={{ textAlign: isRTL ? 'right' : 'left' }}>
                            {language === 'ar' ? 'اسم المدينة' : 'City Name'}
                          </label>
                          <input
                            type="text"
                            name="city_other"
                            value={formData.city_other}
                            onChange={handleChange}
                            disabled={viewMode}
                            placeholder={language === 'ar' ? 'أدخل اسم المدينة' : 'Enter city name'}
                            style={{ 
                              width: '100%',
                              padding: '8px 12px',
                              border: '2px solid #e8e8e8',
                              borderRadius: '8px',
                              fontSize: '0.9rem',
                              background: viewMode ? '#f5f5f5' : '#ffffff',
                              cursor: viewMode ? 'default' : 'text',
                              direction: isRTL ? 'rtl' : 'ltr',
                              textAlign: isRTL ? 'right' : 'left'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ===== SECTION 3: PROPERTY PREFERENCES (Collapsible) ===== */}
              <div style={{ 
                marginTop: '20px', 
                paddingTop: '20px', 
                borderTop: '2px solid #e0e0e0' 
              }}>
                <div
                  onClick={() => toggleSection('preferences')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    padding: '8px',
                    borderRadius: '4px',
                    background: expandedSections.preferences ? '#f9f9f9' : 'transparent'
                  }}
                >
                  <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: '600' }}>
                    {t.interests || 'Interests & Preferences'}
                  </h3>
                  <span style={{ fontSize: '1.2rem' }}>
                    {expandedSections.preferences ? '▼' : '▶'}
                  </span>
                </div>

                {expandedSections.preferences && (
                  <div>
                    {/* Interest Type - Icon Buttons */}
                    <div className="form-group">
                      <label style={{ marginBottom: '10px' }}>{t.interestType || 'Interest Type'}</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {['buy', 'rent', 'sell'].map((type) => {
                          const interestTypes = Array.isArray(formData.interest_type) ? formData.interest_type : (formData.interest_type ? [formData.interest_type] : []);
                          const isSelected = interestTypes.includes(type);
                          const styles = {
                            buy: { color: '#10b981', bgColor: '#d1fae5', hoverBg: '#ecfdf5', hoverBorder: '#34d399', textColor: '#065f46' },
                            rent: { color: '#3b82f6', bgColor: '#dbeafe', hoverBg: '#eff6ff', hoverBorder: '#60a5fa', textColor: '#1e40af' },
                            sell: { color: '#f59e0b', bgColor: '#fef3c7', hoverBg: '#fffbeb', hoverBorder: '#fbbf24', textColor: '#92400e' }
                          };
                          const labels = {
                            buy: { icon: '🏠', text: t.buy || 'Buy' },
                            rent: { icon: '🏘️', text: t.rent || 'Rent' },
                            sell: { icon: '💰', text: t.sell || 'Sell' }
                          };
                          return (
                        <button
                              key={type}
                          type="button"
                              onClick={() => handleInterestTypeToggle(type)}
                          style={{
                                padding: '8px 16px',
                                border: isSelected ? `2px solid ${styles[type].color}` : '1px solid #e5e7eb',
                                borderRadius: '6px',
                                background: isSelected ? styles[type].bgColor : '#fff',
                            cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: isSelected ? '600' : '400',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                                gap: '6px',
                                color: isSelected ? styles[type].textColor : '#4b5563'
                          }}
                          onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = styles[type].hoverBg;
                                  e.currentTarget.style.borderColor = styles[type].hoverBorder;
                            }
                          }}
                          onMouseLeave={(e) => {
                                if (!isSelected) {
                              e.currentTarget.style.background = '#fff';
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                            }
                          }}
                        >
                              <span style={{ fontSize: '1.2rem' }}>{labels[type].icon}</span>
                              <span>{labels[type].text}</span>
                        </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Property Preference - Compact Icon Grid */}
                    <div className="form-group">
                      <label style={{ marginBottom: '10px', fontSize: '0.95rem' }}>{t.propertyPreference || 'Property Preference'}</label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: language === 'ar' 
                          ? 'repeat(auto-fill, minmax(75px, 1fr))' 
                          : 'repeat(auto-fill, minmax(80px, 1fr))',
                        gap: '8px'
                      }}>
                        {[
                          { value: 'residential', icon: '🏘️', label: language === 'ar' ? 'سكني' : 'Residential', color: '#2196F3' },
                          { value: 'apartment', icon: '🏢', label: t.apartment || 'Apartment', color: '#42A5F5' },
                          { value: 'villa', icon: '🏡', label: t.villa || 'Villa', color: '#4CAF50' },
                          { value: 'studio', icon: '🏠', label: t.studio || 'Studio', color: '#26A69A' },
                          { value: 'penthouse', icon: '🏙️', label: t.penthouse || 'Penthouse', color: '#E91E63' },
                          { value: 'townhouse', icon: '🏘️', label: t.townhouse || 'Townhouse', color: '#FF9800' },
                          { value: 'commercial', icon: '🏬', label: t.commercial || 'Commercial', color: '#FF5722' },
                          { value: 'office', icon: '🏢', label: t.office || 'Office', color: '#9C27B0' },
                          { value: 'warehouse', icon: '🏭', label: t.warehouse || 'Warehouse', color: '#795548' },
                          { value: 'land', icon: '🗺️', label: language === 'ar' ? 'أرض سكنية' : 'Land (Residential)', color: '#8BC34A' },
                          { value: 'farmland', icon: '🌾', label: language === 'ar' ? 'أرض زراعية' : 'Farmland', color: '#689F38' },
                          { value: 'other', icon: '➕', label: t.other || 'Other', color: '#607D8B' }
                        ].map((prop) => {
                          const propertyPrefs = Array.isArray(formData.property_preference) ? formData.property_preference : (formData.property_preference ? [formData.property_preference] : []);
                          const isSelected = propertyPrefs.includes(prop.value);
                          return (
                          <button
                            key={prop.value}
                            type="button"
                              onClick={() => handlePropertyPreferenceToggle(prop.value)}
                            style={{
                              padding: '10px 6px',
                                border: isSelected 
                                ? `2px solid ${prop.color}` 
                                : '1.5px solid #e0e0e0',
                              borderRadius: '8px',
                                background: isSelected 
                                ? `${prop.color}15` 
                                : '#fff',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                                fontWeight: isSelected ? '600' : '400',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '4px',
                              minHeight: '65px',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSelected) {
                                e.currentTarget.style.background = '#f9f9f9';
                                e.currentTarget.style.borderColor = prop.color;
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                              }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSelected) {
                                e.currentTarget.style.background = '#fff';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          >
                            <span style={{ fontSize: '1.5rem', lineHeight: '1' }}>{prop.icon}</span>
                            <span style={{ 
                              lineHeight: '1.2', 
                              textAlign: 'center',
                              wordBreak: language === 'ar' ? 'keep-all' : 'normal'
                            }}>
                              {prop.label}
                            </span>
                          </button>
                          );
                        })}
                      </div>
                      {/* Custom property type input when "other" is selected */}
                      {(() => {
                        const propertyPrefs = Array.isArray(formData.property_preference) ? formData.property_preference : (formData.property_preference ? [formData.property_preference] : []);
                        const hasOther = propertyPrefs.includes('other');
                        return hasOther ? (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ 
                              fontSize: '0.9rem', 
                              marginBottom: '6px', 
                              display: 'block',
                              color: '#666',
                              fontWeight: '500'
                            }}>
                              {language === 'ar' ? 'حدد نوع العقار المخصص' : 'Specify custom property type'}
                            </label>
                            <input
                              type="text"
                              name="property_preference_other"
                              value={formData.property_preference_other}
                              onChange={handleChange}
                              placeholder={language === 'ar' ? 'مثال: مزرعة، شاليه، إلخ' : 'e.g., Farm, Chalet, etc.'}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1.5px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                direction: isRTL ? 'rtl' : 'ltr',
                                textAlign: isRTL ? 'right' : 'left'
                              }}
                            />
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <div className="form-group" style={{ width: '280px', maxWidth: '100%' }}>
                      <label style={{ textAlign: isRTL ? 'right' : 'left', fontSize: '0.95rem', marginBottom: '8px' }}>{t.budget || 'Budget'}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                        <div style={{ width: '120px', position: 'relative', flexShrink: 0 }}>
                          <label style={{ 
                            position: 'absolute',
                            top: '8px',
                            left: '0',
                            right: '0',
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            color: '#666',
                            pointerEvents: 'none',
                            fontWeight: '500'
                          }}>
                            {language === 'ar' ? 'الحد الأدنى' : 'Min'}
                          </label>
                          <input
                            type="text"
                            name="budget_min"
                            value={formatNumberWithCommas(formData.budget_min)}
                            onChange={handleBudgetMinChange}
                            disabled={viewMode}
                            placeholder=""
                            inputMode="numeric"
                            style={{ 
                              width: '100%',
                              paddingTop: '24px',
                              paddingBottom: '10px',
                              paddingLeft: isRTL ? '10px' : '10px',
                              paddingRight: isRTL ? '10px' : '10px',
                              fontSize: '0.9rem',
                              height: '50px',
                              background: viewMode ? '#f5f5f5' : '#ffffff',
                              cursor: viewMode ? 'default' : 'text'
                            }}
                          />
                        </div>
                        <span style={{ 
                          color: '#999', 
                          fontSize: '1rem', 
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          –
                        </span>
                        <div style={{ width: '120px', position: 'relative', flexShrink: 0 }}>
                          <label style={{ 
                            position: 'absolute',
                            top: '8px',
                            left: '0',
                            right: '0',
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            color: '#666',
                            pointerEvents: 'none',
                            fontWeight: '500'
                          }}>
                            {language === 'ar' ? 'الحد الأقصى' : 'Max'}
                          </label>
                          <input
                            type="text"
                            name="budget_max"
                            value={formatNumberWithCommas(formData.budget_max)}
                            onChange={handleBudgetMaxChange}
                            disabled={viewMode}
                            placeholder=""
                            inputMode="numeric"
                            style={{ 
                              width: '100%',
                              paddingTop: '24px',
                              paddingBottom: '10px',
                              paddingLeft: isRTL ? '10px' : '10px',
                              paddingRight: isRTL ? '10px' : '10px',
                              fontSize: '0.9rem',
                              height: '50px',
                              background: viewMode ? '#f5f5f5' : '#ffffff',
                              cursor: viewMode ? 'default' : 'text'
                            }}
                          />
                          {isValidBudgetRange() === false && (
                            <span style={{
                              position: 'absolute',
                              right: isRTL ? 'auto' : '8px',
                              left: isRTL ? '8px' : 'auto',
                              top: '28px',
                              color: '#ff4444',
                              fontSize: '1.2rem'
                            }}>✕</span>
                          )}
                        </div>
                        <span style={{ 
                          color: '#666', 
                          fontSize: '0.85rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          fontWeight: '500'
                        }}>
                          {t.currency || (language === 'ar' ? 'ريال' : 'SAR')}
                        </span>
                      </div>
                      {isValidBudgetRange() === false && (
                        <small style={{ color: '#ff4444', fontSize: '0.8rem', marginTop: '6px', display: 'block' }}>
                          {language === 'ar' 
                            ? 'الحد الأقصى يجب أن يكون أكبر من أو يساوي الحد الأدنى'
                            : 'Maximum must be greater than or equal to minimum'}
                        </small>
                      )}
                      {isValidBudgetRange() !== false && (
                      <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '6px', display: 'block' }}>
                        {t.budgetHint || (language === 'ar' ? 'أدخل الأرقام فقط. اترك الحد الأقصى فارغاً للميزانية الثابتة.' : 'Enter numbers only. Leave max empty for fixed budget.')}
                      </small>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ===== SECTION 4: ADDITIONAL INFO (Collapsible) ===== */}
              <div style={{ 
                marginTop: '20px', 
                paddingTop: '20px', 
                borderTop: '2px solid #e0e0e0' 
              }}>
                <div
                  onClick={() => toggleSection('additional')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    padding: '8px',
                    borderRadius: '4px',
                    background: expandedSections.additional ? '#f9f9f9' : 'transparent'
                  }}
                >
                  <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: '600' }}>
                    {t.additionalInfo || 'Additional Information'}
                  </h3>
                  <span style={{ fontSize: '1.2rem' }}>
                    {expandedSections.additional ? '▼' : '▶'}
                  </span>
                </div>

                {expandedSections.additional && (
                  <div>
                    <div className="form-group" style={{ width: '350px', maxWidth: '100%', marginBottom: '20px' }}>
                      <label style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        {language === 'ar' ? 'نوع العميل' : 'Customer Type'}
                      </label>
                      <select
                        name="customer_type"
                        value={formData.customer_type}
                        onChange={handleChange}
                        disabled={viewMode}
                        style={{ 
                          width: '100%',
                          background: viewMode ? '#f5f5f5' : '#ffffff',
                          cursor: viewMode ? 'default' : 'pointer',
                          direction: isRTL ? 'rtl' : 'ltr',
                          textAlign: isRTL ? 'right' : 'left'
                        }}
                      >
                        <option value="prospect">{language === 'ar' ? 'عميل محتمل' : 'Prospect'}</option>
                        <option value="customer">{language === 'ar' ? 'عميل حالي' : 'Customer'}</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ width: '350px', maxWidth: '100%', marginBottom: '20px' }}>
                      <label style={{ textAlign: isRTL ? 'right' : 'left', marginBottom: '10px', display: 'block' }}>
                        {language === 'ar' ? 'المصدر' : 'Source'} ({language === 'ar' ? 'اختياري' : 'Optional'})
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '8px',
                        marginTop: '8px'
                      }}>
                        {sourceOptions.map((option) => {
                          const isSelected = formData.source === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => !viewMode && handleSourceSelect(option.value)}
                              disabled={viewMode}
                              style={{
                                padding: '8px 14px',
                                border: isSelected ? '2px solid #f97316' : '1px solid #e5e7eb',
                                borderRadius: '6px',
                                background: isSelected ? '#fff4e6' : '#ffffff',
                                cursor: viewMode ? 'default' : 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: isSelected ? '600' : '400',
                                color: isSelected ? '#f97316' : '#4b5563',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                if (!viewMode && !isSelected) {
                                  e.currentTarget.style.background = '#f9fafb';
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!viewMode && !isSelected) {
                                  e.currentTarget.style.background = '#ffffff';
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                }
                              }}
                            >
                              <span style={{ fontSize: '1rem' }}>{option.icon}</span>
                              <span>{option.label[language]}</span>
                            </button>
                          );
                        })}
                      </div>
                      {formData.source === 'other' && (
                        <input
                          type="text"
                          name="source_other"
                          value={formData.source_other || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, source: 'other', source_other: e.target.value }))}
                          disabled={viewMode}
                          placeholder={language === 'ar' ? 'أدخل المصدر' : 'Enter source'}
                          style={{ 
                            width: '100%',
                            marginTop: '10px',
                            padding: '8px 12px',
                            border: '2px solid #e8e8e8',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            background: viewMode ? '#f5f5f5' : '#ffffff',
                            cursor: viewMode ? 'default' : 'text',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                        />
                      )}
                    </div>

                    <div className="form-group" style={{ width: '350px', maxWidth: '100%' }}>
                      <label style={{ textAlign: isRTL ? 'right' : 'left' }}>{t.notes}</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        disabled={viewMode}
                        placeholder={t.notesPlaceholder || (language === 'ar' ? 'ملاحظات إضافية حول العميل...' : 'Additional notes about the client...')}
                        rows={4}
                        style={{ 
                          resize: viewMode ? 'none' : 'vertical',
                          width: '100%',
                          background: viewMode ? '#f5f5f5' : '#ffffff',
                          cursor: viewMode ? 'default' : 'text',
                          direction: isRTL ? 'rtl' : 'ltr',
                          textAlign: isRTL ? 'right' : 'left'
                        }}
                      />
                    </div>
                    
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== ACTION BUTTONS ===== */}
          <div className="form-actions" style={{ 
            marginTop: '30px', 
            paddingTop: '25px', 
            borderTop: '2px solid #e0e0e0',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <button 
              type="button" 
              onClick={onClose}
              style={{
                padding: '8px 20px',
                fontSize: '0.85rem',
                fontWeight: viewMode ? '600' : '500',
                border: 'none',
                borderRadius: '6px',
                background: viewMode ? '#f59e0b' : '#fff',
                color: viewMode ? 'white' : '#6b7280',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: viewMode ? 'none' : '1px solid #e5e7eb'
              }}
              onMouseEnter={(e) => {
                if (!viewMode) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.color = '#374151';
                } else {
                  e.currentTarget.style.background = '#d97706';
                }
              }}
              onMouseLeave={(e) => {
                if (!viewMode) {
                e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#6b7280';
                } else {
                  e.currentTarget.style.background = '#f59e0b';
                }
              }}
            >
              {viewMode ? (language === 'ar' ? 'إغلاق' : 'Close') : t.cancel}
            </button>
            {!viewMode && (
              <>
            {!client && (
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
                style={{ 
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      background: loading ? '#fbbf24' : '#fef3c7',
                      color: loading ? '#ffffff' : '#d97706',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                        e.currentTarget.style.background = '#fde68a';
                        e.currentTarget.style.borderColor = '#d97706';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                        e.currentTarget.style.background = '#fef3c7';
                        e.currentTarget.style.borderColor = '#f59e0b';
                  }
                }}
              >
                {loading ? t.saving : (t.saveAndAddAnother || 'Save & Add Another')}
              </button>
            )}
            <button 
              type="submit" 
              disabled={loading}
              style={{
                    padding: '8px 20px',
                    fontSize: '0.85rem',
                fontWeight: '600',
                border: 'none',
                    borderRadius: '6px',
                    background: loading ? '#9ca3af' : '#f59e0b',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                      e.currentTarget.style.background = '#d97706';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                      e.currentTarget.style.background = '#f59e0b';
                }
              }}
            >
              {loading ? t.saving : t.save}
            </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;

